// Measuring on a photo. A rectangle of known size in the photo (an A4 sheet, a card, a tile,
// a door measured once with the tape) fixes a perspective transform (homography) from photo
// pixels to millimetres on that surface. Any two points on the same flat surface can then be
// measured, however skewed the photo was taken. Points off that surface (a wall seen on a
// floor reference) are measured wrong, which the UI says.

const REFERENCES = {
  a4: { width: 210, height: 297 },
  a3: { width: 297, height: 420 },
  // ISO/IEC 7810 ID-1: bank, credit and ID cards
  card: { width: 85.6, height: 53.98 }
};

// Tap precision in screen pixels for the uncertainty estimate (with the magnifier).
const TAP_SIGMA = 1.2;
// A measured point this many reference half-diagonals away from the reference is an
// extrapolation that magnifies every error.
const FAR_FACTOR = 4;

const solveLinear = (matrix, vector) => {
  const n = vector.length;
  const a = matrix.map((row, i) => [...row, vector[i]]);
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let row = col + 1; row < n; row += 1) {
      if (Math.abs(a[row][col]) > Math.abs(a[pivot][col])) pivot = row;
    }
    if (Math.abs(a[pivot][col]) < 1e-12) return null;
    [a[col], a[pivot]] = [a[pivot], a[col]];
    for (let row = 0; row < n; row += 1) {
      if (row === col) continue;
      const factor = a[row][col] / a[col][col];
      for (let k = col; k <= n; k += 1) a[row][k] -= factor * a[col][k];
    }
  }
  return a.map((row, i) => row[n] / row[i]);
};

// Hartley normalisation: centre the points and scale them to an average distance of √2, which
// keeps the linear system well conditioned for 4000-pixel photos and millimetre targets.
const normalizer = points => {
  const cx = points.reduce((sum, p) => sum + p[0], 0) / points.length;
  const cy = points.reduce((sum, p) => sum + p[1], 0) / points.length;
  const mean =
    points.reduce((sum, p) => sum + Math.hypot(p[0] - cx, p[1] - cy), 0) / points.length || 1;
  const s = Math.SQRT2 / mean;
  return {
    apply: p => [(p[0] - cx) * s, (p[1] - cy) * s],
    matrix: [
      [s, 0, -s * cx],
      [0, s, -s * cy],
      [0, 0, 1]
    ],
    inverse: [
      [1 / s, 0, cx],
      [0, 1 / s, cy],
      [0, 0, 1]
    ]
  };
};

const multiply = (a, b) =>
  a.map(row => b[0].map((_, j) => row.reduce((sum, value, k) => sum + value * b[k][j], 0)));

/** 3×3 matrix that maps each of the four `from` points onto the matching `to` point. */
const homography = (from, to) => {
  const nf = normalizer(from);
  const nt = normalizer(to);
  const rows = [];
  const values = [];
  from.forEach((point, i) => {
    const [x, y] = nf.apply(point);
    const [u, v] = nt.apply(to[i]);
    rows.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    values.push(u);
    rows.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    values.push(v);
  });
  const h = solveLinear(rows, values);
  if (!h) return null;
  const normalized = [
    [h[0], h[1], h[2]],
    [h[3], h[4], h[5]],
    [h[6], h[7], 1]
  ];
  return multiply(nt.inverse, multiply(normalized, nf.matrix));
};

const project = (h, [x, y]) => {
  const w = h[2][0] * x + h[2][1] * y + h[2][2];
  return [(h[0][0] * x + h[0][1] * y + h[0][2]) / w, (h[1][0] * x + h[1][1] * y + h[1][2]) / w];
};

const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

/** Sorts four taps into top-left, top-right, bottom-right, bottom-left (clockwise on screen). */
const orderCorners = points => {
  const cx = points.reduce((sum, p) => sum + p.x, 0) / points.length;
  const cy = points.reduce((sum, p) => sum + p.y, 0) / points.length;
  const sorted = [...points].sort(
    (a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx)
  );
  const start = sorted.reduce(
    (best, p, i) => (p.x + p.y < sorted[best].x + sorted[best].y ? i : best),
    0
  );
  return [...sorted.slice(start), ...sorted.slice(0, start)];
};

const isConvex = corners => {
  const pts = corners.map(p => [p.x, p.y]);
  const signs = pts.map((p, i) => Math.sign(cross(p, pts[(i + 1) % 4], pts[(i + 2) % 4])));
  return signs.every(sign => sign !== 0 && sign === signs[0]);
};

/**
 * Where the ordered corners go in millimetres. The longer pair of photo edges gets the longer
 * side of the reference; `swap` overrides that guess for extreme angles.
 */
const referenceTargets = (corners, { width, height }, swap = false) => {
  const d = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const [tl, tr, br, bl] = corners;
  const horizontal = d(tl, tr) + d(bl, br);
  const vertical = d(tl, bl) + d(tr, br);
  const long = Math.max(width, height);
  const short = Math.min(width, height);
  let [w, h] = horizontal >= vertical ? [long, short] : [short, long];
  if (swap) [w, h] = [h, w];
  return [
    [0, 0],
    [w, 0],
    [w, h],
    [0, h]
  ];
};

// Small seeded generator so the uncertainty shown does not flicker between renders.
const random = seed => {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
};

const gaussian = next => {
  const u = Math.max(next(), 1e-12);
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * next());
};

/**
 * Length in mm between two photo points ({x, y, s}; s = tap uncertainty in photo pixels), with
 * its standard uncertainty from simulating tap errors on all six points.
 */
const measureLine = (corners, targets, a, b, samples = 300) => {
  const from = corners.map(p => [p.x, p.y]);
  const h = homography(from, targets);
  if (!h) return null;
  const pa = project(h, [a.x, a.y]);
  const pb = project(h, [b.x, b.y]);
  const length = Math.hypot(pa[0] - pb[0], pa[1] - pb[1]);

  const next = random(Math.round(a.x * 7 + a.y * 13 + b.x * 17 + b.y * 19));
  const jitter = p => [p.x + gaussian(next) * (p.s ?? 1), p.y + gaussian(next) * (p.s ?? 1)];
  let sum = 0;
  let sumSq = 0;
  let n = 0;
  for (let i = 0; i < samples; i += 1) {
    const hj = homography(corners.map(jitter), targets);
    if (!hj) continue;
    const qa = project(hj, jitter(a));
    const qb = project(hj, jitter(b));
    const value = Math.hypot(qa[0] - qb[0], qa[1] - qb[1]);
    if (!Number.isFinite(value)) continue;
    sum += value;
    sumSq += value * value;
    n += 1;
  }
  const mean = n ? sum / n : length;
  const sigma = n > 1 ? Math.sqrt(Math.max(sumSq / n - mean * mean, 0)) : null;

  const [w, hgt] = [targets[2][0], targets[2][1]];
  const half = Math.hypot(w, hgt) / 2;
  const far = [pa, pb].some(p => Math.hypot(p[0] - w / 2, p[1] - hgt / 2) > FAR_FACTOR * half);
  return { length, sigma, far };
};

export {
  REFERENCES,
  TAP_SIGMA,
  FAR_FACTOR,
  solveLinear,
  homography,
  project,
  orderCorners,
  isConvex,
  referenceTargets,
  measureLine
};
